import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Cpu,
  Zap,
  HeartPulse,
  Calendar,
  Download,
  MapPin,
  Compass,
  Handshake,
  Gauge,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { INSIGHTS } from "@/data/insights";
import { ASSETS } from "@/data/assets";
import NewsletterForm from "@/components/NewsletterForm";

const HERO_PORTRAIT = ASSETS.portrait;

const AI_ABSTRACT =
  "https://images.unsplash.com/photo-1674027444485-cec3da58eef4";

const CALENDAR_URL = "https://calendar.app.google/6z7bgKTBSgN54krq8";
const ENGAGEMENT_URL = "/vincent-palomarez-engagement.webp";

const SECTORS = [
  {
    icon: Cpu,
    label: "Technology",
    detail: "Industrial software & IoT",
  },
  {
    icon: Zap,
    label: "Energy",
    detail: "Digital transformation",
  },
  {
    icon: HeartPulse,
    label: "Medical",
    detail: "Enterprise services",
  },
];

const OFFERINGS = [
  {
    icon: Compass,
    title: "Sales Leadership & Revenue Acceleration",
    body: "Strategic and hands-on support to build or strengthen your sales function — pipeline discipline, forecasting accuracy, team coaching, and go-to-market execution.",
  },
  {
    icon: Handshake,
    title: "Fractional & Advisory Engagements",
    body: "Available for selective fractional VP Sales / Sales Director roles and advisory board positions. Designed for high impact in 4–5 days per month.",
  },
  {
    icon: Gauge,
    title: "Sales Process & Pipeline Audits",
    body: "Quick diagnostic of your current sales motion with a clear 30/60/90-day roadmap to improve results.",
  },
  {
    icon: Users,
    title: "Team Transitions & Enablement",
    body: "Help engineering or product-led teams shift to modern SaaS and IoT selling motions with joint go-to-market strategies.",
  },
];

const RESULTS = [
  {
    metric: "$50M+",
    body: "Secured across 14 quarters by building new account management and pipeline processes.",
  },
  {
    metric: "AI/ML",
    body: "Led successful transition of sales team to AI/ML-driven Industrial Automation and SaaS solutions.",
  },
  {
    metric: "3× Growth",
    body: "Delivered 3× account growth, $16M+ in sales, and 25% annual profit growth through strategic partnerships.",
  },
  {
    metric: "~$150M",
    body: "Generated ~$150M in contracts in the first six months with a 40% profit increase.",
  },
  {
    metric: "10× Pipeline",
    body: "Built 10× pipeline versus initial projections while exceeding gross profit targets in a new U.S. market entry.",
  },
  {
    metric: "$90M",
    body: "Secured $90M in new contracts in first six months and exceeded annual targets by 15%+ while developing sales talent.",
  },
];

export default function Home() {
  const latest = INSIGHTS.slice(0, 3);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" aria-hidden />
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 pb-20 lg:pt-28 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 animate-fade-up">
              <p className="font-mono-ui text-[11px] uppercase tracking-[0.28em] text-muted-foreground mb-6 flex items-center gap-3">
                <span className="inline-block w-8 h-px bg-foreground/40" />
                Enterprise Sales · Technology · Energy · Medical
              </p>
              <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
                Vincent Palomarez —{" "}
                <span className="relative inline-block">
                  <span className="relative z-10">Enterprise Sales Leader</span>
                  <span
                    className="absolute inset-x-0 bottom-1 h-3 bg-accent/25 -z-0"
                    aria-hidden
                  />
                </span>
              </h1>
              <p className="mt-8 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">
                15+ years scaling high-performing teams and driving revenue
                growth in{" "}
                <span className="text-foreground font-medium">
                  Energy, Industrial IoT, and SaaS
                </span>
                . Open to selective fractional VP Sales and advisory
                engagements (4–5 days/month).
              </p>

              <p
                data-testid="hero-location"
                className="mt-5 inline-flex items-center gap-2 font-mono-ui text-[11px] uppercase tracking-[0.25em] text-muted-foreground"
              >
                <MapPin className="h-3.5 w-3.5 text-accent" />
                Cypress, TX
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-3">
                <a
                  href={CALENDAR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    data-testid="hero-book-call-button"
                    size="lg"
                    className="rounded-sm group gap-2"
                  >
                    <Calendar className="h-4 w-4" />
                    Book a 20-Minute Strategy Call
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </a>
                <a href={ENGAGEMENT_URL} target="_blank" rel="noopener noreferrer">
                  <Button
                    data-testid="hero-view-engagement-button"
                    size="lg"
                    variant="outline"
                    className="rounded-sm group gap-2"
                  >
                    <Download className="h-4 w-4 text-ai" />
                    My Engagement
                  </Button>
                </a>
              </div>

              {/* Signal strip */}
              <div className="mt-14 grid grid-cols-3 max-w-xl divide-x divide-border border-y border-border">
                {SECTORS.map((s) => (
                  <div key={s.label} className="py-4 px-4 first:pl-0">
                    <div className="flex items-center gap-2">
                      <s.icon className="h-3.5 w-3.5 text-accent" />
                      <p className="font-mono-ui text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        {s.label}
                      </p>
                    </div>
                    <p className="text-sm mt-1 text-foreground">{s.detail}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Portrait / AI accent */}
            <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "120ms" }}>
              <div className="relative aspect-[4/5] w-full max-w-md ml-auto">
                <div className="absolute -inset-1 bg-gradient-to-br from-accent/30 via-ai/20 to-transparent blur-xl opacity-70" aria-hidden />
                <div className="relative overflow-hidden border border-border">
                  <img
                    src={HERO_PORTRAIT}
                    alt="Vincent Palomarez"
                    loading="eager"
                    decoding="async"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "50% 15%" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent" />
                </div>
                {/* Overlay data chip */}
                <div className="absolute -left-6 bottom-8 hidden sm:flex items-center gap-3 border border-border bg-background px-4 py-3 shadow-sm">
                  <span className="inline-flex h-2 w-2 rounded-full bg-accent animate-pulse" />
                  <div>
                    <p className="font-mono-ui text-[10px] uppercase tracking-widest text-muted-foreground">
                      Framework
                    </p>
                    <p className="text-sm font-medium">Corporate Athlete 2.0</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW I HELP COMPANIES GROW */}
      <section
        data-testid="section-how-i-help"
        className="border-y border-border bg-secondary/40"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-accent">
              // 001 — Offerings
            </p>
            <h2 className="mt-4 font-display font-black text-3xl lg:text-4xl tracking-tight">
              How I Help Companies Grow
            </h2>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed max-w-sm">
              Four ways I partner with founders and executives to unlock
              revenue — from diagnostic to hands-on leadership.
            </p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-6">
            {OFFERINGS.map((o) => (
              <div
                key={o.title}
                data-testid={`offering-${o.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                className="border border-border p-6 bg-background hover:-translate-y-1 hover:shadow-sm transition-all"
              >
                <o.icon className="h-5 w-5 text-accent" />
                <h3 className="mt-4 font-display font-bold text-lg leading-tight">
                  {o.title}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  {o.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SELECTED RESULTS */}
      <section
        data-testid="section-selected-results"
        className="mx-auto max-w-7xl px-6 lg:px-10 py-20"
      >
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-accent">
              // 002 — Selected Results
            </p>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-4xl tracking-tight">
              Numbers from the last 15 years.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-sm text-muted-foreground leading-relaxed">
              A snapshot of the outcomes I've delivered across enterprise
              sales, market entries, and team transformations.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
          {RESULTS.map((r, idx) => (
            <div
              key={idx}
              data-testid={`result-item-${idx}`}
              className="border-r border-b border-border p-6 hover:bg-secondary/40 transition-colors"
            >
              <p className="font-display font-black text-3xl lg:text-4xl tracking-tight text-foreground">
                {r.metric}
              </p>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                {r.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW WE WORK — FLEXIBLE ENGAGEMENTS */}
      <section
        data-testid="section-how-we-work"
        className="border-y border-border bg-secondary/30"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-ai">
              // 003 — How We Work
            </p>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-4xl tracking-tight leading-tight">
              Flexible Engagements That Fit Your Needs
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-muted-foreground leading-relaxed">
            <p>
              I work with a limited number of companies at a time. Engagements
              are structured for high impact with a limited time commitment —
              typically in the range of{" "}
              <span className="text-foreground font-medium">
                4–5 days per month
              </span>
              . This model works well for companies that need senior-level
              sales leadership, strategic guidance, or help scaling without
              adding a full-time executive.
            </p>
            <div className="border border-border bg-background p-6">
              <p className="font-mono-ui text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Typical Flow
              </p>
              <p className="mt-3 text-foreground">
                Diagnostic &amp; roadmap (first 30 days) → Execution, coaching
                &amp; process building → Transition to your internal team or
                ongoing advisory support.
              </p>
            </div>
            <p>
              I'm based in the{" "}
              <span className="text-foreground font-medium">
                Cypress/Houston area
              </span>{" "}
              and prioritize work that delivers clear revenue results while
              respecting family time.
            </p>

            <div
              data-testid="prepare-for-call"
              className="border border-border bg-background p-6"
            >
              <p className="font-mono-ui text-[10px] uppercase tracking-[0.22em] text-accent">
                Before we meet
              </p>
              <h3 className="mt-3 font-display font-bold text-xl text-foreground">
                How to Prepare for Our Call
              </h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                To make the most of our 20 minutes together, please come ready
                with:
              </p>
              <ul className="mt-4 space-y-3 text-sm text-foreground">
                <li className="flex gap-3">
                  <span
                    className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-accent shrink-0"
                    aria-hidden
                  />
                  <span className="leading-relaxed">
                    A quick overview of your current sales motion or biggest
                    revenue challenge right now.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span
                    className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-accent shrink-0"
                    aria-hidden
                  />
                  <span className="leading-relaxed">
                    Your top 1–2 priorities for the next 6–12 months.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span
                    className="mt-2 inline-block h-1.5 w-1.5 rounded-full bg-accent shrink-0"
                    aria-hidden
                  />
                  <span className="leading-relaxed">
                    Any specific questions you have about fractional
                    leadership, advisory support, or working together.
                  </span>
                </li>
              </ul>
            </div>

            <div className="pt-2 flex flex-wrap gap-3">
              <a
                href={CALENDAR_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  data-testid="how-we-work-book-call-button"
                  size="lg"
                  className="rounded-sm gap-2 group"
                >
                  <Calendar className="h-4 w-4" />
                  Book a 20-Minute Strategy Call
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </a>
              <Link to="/contact">
                <Button
                  data-testid="how-we-work-contact-button"
                  size="lg"
                  variant="outline"
                  className="rounded-sm gap-2"
                >
                  Send a message
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* LATEST INSIGHTS PREVIEW */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-accent">
              // 004 — Insights
            </p>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-4xl tracking-tight">
              Recent thinking
            </h2>
          </div>
          <Link
            to="/insights"
            data-testid="home-see-all-insights"
            className="hidden sm:inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground group"
          >
            View all
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {latest.map((post) => (
            <Link
              key={post.slug}
              to={`/insights/${post.slug}`}
              data-testid={`home-insight-card-${post.slug}`}
              className="group border border-border bg-background overflow-hidden hover:-translate-y-1 transition-transform"
            >
              <div className="aspect-[16/10] overflow-hidden bg-secondary">
                <img
                  src={post.hero}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <p className="font-mono-ui text-[10px] uppercase tracking-widest text-accent">
                  {post.tag} · {post.readTime}
                </p>
                <h3 className="mt-3 font-display font-bold text-lg leading-tight">
                  {post.title}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                  {post.excerpt}
                </p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-foreground">
                  Read
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="border-t border-border bg-secondary/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-20">
          <NewsletterForm source="home" />
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-ai">
              // 005 — Let's build
            </p>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-4xl tracking-tight max-w-2xl">
              Have a growth problem worth solving?
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl">
              I work with executives and go‑to‑market teams across Technology,
              Energy, and Medical. If AI, leadership development, or
              sustainable high performance is on your roadmap — let's talk.
            </p>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end">
            <div className="relative w-full max-w-md border border-border p-6 bg-background overflow-hidden">
              <img
                src={AI_ABSTRACT}
                alt="AI accent"
                className="absolute inset-0 w-full h-full object-cover opacity-20"
              />
              <div className="relative">
                <p className="font-mono-ui text-[10px] uppercase tracking-widest text-muted-foreground">
                  Direct line
                </p>
                <h3 className="mt-2 font-display font-bold text-xl">
                  Start a conversation
                </h3>
                <Link to="/contact">
                  <Button
                    data-testid="home-cta-contact-button"
                    size="lg"
                    className="mt-5 rounded-sm gap-2 w-full sm:w-auto"
                  >
                    Get in touch
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
